/**
 * <LiveSessionsListPage> — « Tous mes lives » (page).
 *
 * Un `<Header>` (titre + fermer / rafraîchir) au-dessus des séances rangées par jour. Depuis la
 * passe de parité, la page reprend le VISUEL DE L'ACCUEIL : des cartes-listes de lignes compactes,
 * plus la grosse carte à menu « ⋮ ». Toucher une ligne ouvre les actions de CETTE séance.
 *
 * Quelle séance est ouverte est un état de VUE, pas une donnée : la page le garde, comme
 * `<ParentHomePage>` garde le contrôle ouvert. L'écran, lui, ne reçoit que le geste choisi.
 *
 * Une séance SUSPENDUE n'ouvre pas ses actions — aucune n'aboutirait tant que le paiement n'est
 * pas réglé : le clic repart tel quel à l'appelant (`onBlockedSessionPress`), qui propose la
 * régularisation. La page ne décide rien : elle lit un statut qu'elle affiche déjà.
 *
 * **La même page pour l'élève** (`voice="student"`) : le titre et les mots de l'état vide sont
 * déjà les siens. Ce qui change : les gestes qu'il a (`allowedActions`, ceux de son menu
 * d'aujourd'hui), la ligne « Rattrapage de l'absence du … », qu'il lit sur chaque séance, et
 * l'état vide, posé en `<EmptyState>`. Depuis que ses prochains lives ont quitté « Messages »
 * (Wajih, 18/10), la page porte aussi son « Planifier un rattrapage » (`onPlanRecovery`), en tête.
 */
import React from "react"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { isPaymentBlocked, isSessionFinished } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { EmptyState } from "../../molecules/EmptyState"
import { Header } from "../../molecules/Header"
import { RefreshButton } from "../../molecules/RefreshButton"
import { GroupedLiveSessionsListSection } from "../../templates/GroupedLiveSessionsListSection"
import { LiveSessionActionsSheet, type LiveSessionAction } from "../../templates/LiveSessionActionsSheet"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { PlanRecoveryButton } from "../../templates/UpcomingSessionsSection"
import { type UILiveSession, type UILiveSessionGroup } from "../../../ports/primary/ui/types"
import { type Voice } from "../../utils/voice"
import { LiveSessionsListSkeleton } from "./LiveSessionsListSkeleton"

/** Les mêmes mots pour les deux voix : ce sont ceux de l'écran d'aujourd'hui. */
const EMPTY_MESSAGE = "Aucune session planifiée"

export type LiveSessionsListPageProps = {
    groups: UILiveSessionGroup[]
    /** Premier chargement, rien à montrer : la page montre son squelette. */
    loading?: boolean
    /** Un rafraîchissement alors que les séances sont à l'écran : seule la flèche de l'en-tête tourne. */
    refreshing?: boolean
    error?: unknown
    onRetry?: () => void
    onClose?: () => void
    onRefresh?: () => void
    /** Le geste choisi dans la feuille d'actions, avec la séance déjà attachée. */
    onSessionAction?: (session: UILiveSession, action: LiveSessionAction) => void
    /**
     * Prénom de l'enfant quand le parent peut basculer sur son espace pour le faire entrer en
     * live : la feuille d'actions y gagne « Rejoindre en tant que … ». ABSENT = pas de ligne.
     */
    joinAsChildFirstname?: string
    /**
     * Le clic sur une séance suspendue pour paiement. ABSENT = la feuille s'ouvre comme pour les
     * autres — elle sera vide, le statut ne laissant aucun geste possible.
     */
    onBlockedSessionPress?: (session: UILiveSession) => void
    /** À qui la page parle — défaut `"parent"`. */
    voice?: Voice
    /** Les gestes que ce rôle a sur une séance (`<LiveSessionActionsSheet allowedActions>`). ABSENT = tous. */
    allowedActions?: readonly LiveSessionAction[]
    /** « Planifier un rattrapage », en tête de la page. ABSENT = aucune absence à rattraper. */
    onPlanRecovery?: () => void
    /** Les absences rattrapables dès maintenant : leur nombre s'écrit sur le bouton. */
    recoverableNowCount?: number
    /**
     * Le toucher d'une séance terminée, qui n'a aucun geste : l'appelant dit pourquoi (« Cette
     * session est clôturée »). ABSENT = la ligne ne réagit pas.
     */
    onFinishedSessionPress?: (session: UILiveSession) => void
    testID?: string
}

export function LiveSessionsListPage(props: LiveSessionsListPageProps) {
    const {
        groups,
        loading = false,
        refreshing = false,
        error,
        onRetry,
        onClose,
        onRefresh,
        onSessionAction,
        joinAsChildFirstname,
        onBlockedSessionPress,
        voice = "parent",
        allowedActions,
        onPlanRecovery,
        recoverableNowCount = 0,
        onFinishedSessionPress,
        testID,
    } = props
    const isEmpty = groups.length === 0

    const [openSessionId, setOpenSessionId] = React.useState<UILiveSession["id"] | null>(null)
    const openSession = groups.flatMap((group) => group.sessions).find((s) => s.id === openSessionId) ?? null

    const selectSession = (session: UILiveSession) => {
        if (onFinishedSessionPress != null && isSessionFinished(session)) {
            onFinishedSessionPress(session)
            return
        }
        if (onBlockedSessionPress != null && isPaymentBlocked(session)) {
            onBlockedSessionPress(session)
            return
        }
        setOpenSessionId(session.id)
    }

    const sessionsList = isEmpty ? (
        voice === "student" ? (
            <EmptyState title={EMPTY_MESSAGE} />
        ) : (
            <Text color="text-muted">{EMPTY_MESSAGE}</Text>
        )
    ) : (
        <GroupedLiveSessionsListSection
            groups={groups}
            onSelectSession={onSessionAction != null ? selectSession : undefined}
            showRecoveryInfo={voice === "student"}
        />
    )

    const header = (
        <Header
            title="Tous mes lives"
            left={<IconButton iconName="times" appearance="transparent" iconColor="primary" onPress={onClose} />}
            right={onRefresh != null ? <RefreshButton refreshing={refreshing} onRefresh={onRefresh} /> : undefined}
        />
    )

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<LiveSessionsListSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            {onPlanRecovery != null ? (
                <Stack gap="6">
                    <PlanRecoveryButton count={recoverableNowCount} onPress={onPlanRecovery} />
                    {sessionsList}
                </Stack>
            ) : (
                sessionsList
            )}

            <LiveSessionActionsSheet
                visible={openSession != null}
                onClose={() => setOpenSessionId(null)}
                session={openSession}
                joinAsChildFirstname={joinAsChildFirstname}
                voice={voice}
                allowedActions={allowedActions}
                onAction={(action) => {
                    setOpenSessionId(null)
                    if (openSession != null) onSessionAction?.(openSession, action)
                }}
            />
        </PageTemplate>
    )
}
